import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { Box, Group, Text } from '@mantine/core';

export type Boxplot = { low: number; q1?: number; median?: number; q3?: number; high: number };
export type SeriesPoint = { timestamp: string; [key: string]: Boxplot | string | number | null | undefined };

type Shape = 'line' | 'step' | 'bar' | 'arearange' | 'boxplot';

export interface OverlayLines {
  metrics: string[];
  colors?: Record<string, string>;
  shapes?: Record<string, Shape>;
}

interface Props {
  data: SeriesPoint[];
  metrics?: string[];
  metric?: string;
  mode?: 'line' | 'arearange' | 'candlestick' | 'bar' | 'boxplot';
  resolution?: string;
  colors?: Record<string, string>;
  shapes?: Record<string, Shape>;
  units?: Record<string, string>;
  displayNameMap?: Record<string, string>;
  overlayLines?: OverlayLines;
  axes?: Record<string, 'left' | 'right' | 'both'>;
  unitAxisSides?: Record<string, 'left' | 'right'>;
  dateOnlyTicks?: boolean;
  height?: number;
  showLegend?: boolean;
  showTrends?: boolean;
  trendTargets?: 'all' | 'overlay' | 'base';
}

function parseTs(ts: string | number): number | null {
  if (typeof ts === 'number') return Number.isFinite(ts) ? ts : null;
  let s = String(ts).replace(' ', 'T');
  if (!/[zZ]|[-+]\d{2}:?\d{2}$/.test(s)) s = s + 'Z';
  s = s.replace(/([+-]\d{2}:?\d{2})Z$/, '$1');
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

const UnifiedEChartsV2: React.FC<Props> = ({
  data,
  metrics,
  metric,
  mode,
  colors = {},
  shapes = {},
  units = {},
  displayNameMap = {},
  overlayLines,
  axes = {},
  unitAxisSides = {},
  dateOnlyTicks,
  height = 360,
  showLegend = true,
  showTrends = false,
  trendTargets = 'overlay',
}) => {
  const labels = useMemo(
    () => (metrics && metrics.length ? metrics : (metric ? [metric] : [])).filter(Boolean) as string[],
    [metrics, metric]
  );

  const overlayList = useMemo(() => {
    const raw = Array.from(new Set(overlayLines?.metrics ?? []));
    return raw.filter((lbl) => !(labels as string[]).includes(lbl));
  }, [overlayLines?.metrics, labels]);

  const isDark = useMemo(() => {
    return document.documentElement.classList?.contains('mantine-dark')
      || window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
  }, []);

  const { yAxes, axisIndexFor } = useMemo(() => {
    const yAxes: Array<{
      type: 'value';
      name: string;
      position: 'left' | 'right';
      axisLine: { show: boolean; lineStyle: { color: string } };
      axisLabel: { color: string };
      splitLine: { lineStyle: { color: string } };
    }> = [];
    const axisIndexFor = new Map<string, number>();

    const getUnit = (lbl: string) => (units && units[lbl] ? String(units[lbl]).trim() : '');

    const allLabels: string[] = [...labels, ...overlayList];
    const seenUnits = new Set<string>();
    const orderUnits: string[] = [];
    allLabels.forEach((lbl) => {
      const u = getUnit(lbl);
      if (!seenUnits.has(u)) { seenUnits.add(u); orderUnits.push(u); }
    });
    if (orderUnits.length === 0) orderUnits.push('');

    orderUnits.forEach((u, i) => {
      const preferred = unitAxisSides[u];
      const position: 'left' | 'right' = preferred ? preferred : (i === 0 ? 'left' : 'right');
      yAxes[i] = {
        type: 'value',
        name: u || '',
        position,
        axisLine: { show: true, lineStyle: { color: isDark ? '#6b7280' : '#d1d5db' } },
        axisLabel: { color: isDark ? '#e5e7eb' : '#000' },
        splitLine: { lineStyle: { color: isDark ? '#374151' : '#e5e7eb' } },
      };
    });

    labels.forEach((lbl) => {
      const u = getUnit(lbl);
      const idx = orderUnits.indexOf(u);
      if (idx >= 0) axisIndexFor.set(lbl, idx);
      else axisIndexFor.set(lbl, (axes[lbl] || 'left') === 'right' ? 1 : 0);
    });
    overlayList.forEach((lbl) => {
      const u = getUnit(lbl);
      const idx = orderUnits.indexOf(u);
      if (idx >= 0) axisIndexFor.set(lbl, idx);
      else axisIndexFor.set(lbl, (axes[lbl] || 'right') === 'right' ? (orderUnits.length > 1 ? 1 : 0) : 0);
    });

    return { yAxes, axisIndexFor };
  }, [labels, overlayList, units, unitAxisSides, axes, isDark]);

  const shapeDefault: Shape = useMemo(() => {
    switch (mode) {
      case 'arearange': return 'arearange';
      case 'boxplot': return 'boxplot';
      case 'bar': return 'bar';
      case 'candlestick': return 'boxplot';
      default: return 'line';
    }
  }, [mode]);

  const series: Array<Record<string, unknown>> = useMemo(() => {
    const baseSeries: Array<Record<string, unknown>> = [];
    const overlaySeries: Array<Record<string, unknown>> = [];
    const emitted = new Set<string>();

    const emit = (s: Record<string, unknown>, overlayFlag: boolean) => {
      const nm = String(s.name || '');
      const typ = String(s.type || 'line');
      // Use full name to allow multiple sensors/devices with same metric
      const key = `${nm}::${typ}`;
      if (emitted.has(key)) return;
      emitted.add(key);
      if (overlayFlag) overlaySeries.push(s);
      else baseSeries.push(s);
    };

    const shouldTrend = (isOverlay: boolean) => {
      if (!showTrends) return false;
      if (trendTargets === 'all') return true;
      if (trendTargets === 'overlay') return isOverlay;
      if (trendTargets === 'base') return !isOverlay;
      return false;
    };

    const linReg = (pts: Array<[number, number]>) => {
      const n = pts.length;
      if (n < 2) return null;
      let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
      for (const [x, y] of pts) { sumX += x; sumY += y; sumXY += x * y; sumXX += x * x; }
      const denom = n * sumXX - sumX * sumX;
      if (denom === 0) return null;
      const m = (n * sumXY - sumX * sumY) / denom;
      const b = (sumY - m * sumX) / n;
      const xs = pts.map(p => p[0]);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      return { m, b, x0: minX, x1: maxX };
    };

    const colorFor = (lbl: string, isOverlay: boolean) => (isOverlay ? (overlayLines?.colors?.[lbl] ?? colors[lbl]) : colors[lbl]);
    const shapeFor = (lbl: string, isOverlay: boolean): Shape => (isOverlay ? (overlayLines?.shapes?.[lbl] ?? shapes[lbl] ?? shapeDefault) : (shapes[lbl] ?? shapeDefault));

    const renderLabel = (label: string, isOverlay: boolean) => {
      const shape = shapeFor(label, isOverlay);
      const color = colorFor(label, isOverlay);
      const displayName = displayNameMap[label] || label;
      const unit = units[label] || '';

      if (shape === 'boxplot') {
        const boxData: Array<[number, number, number, number, number, number]> = [];
        const ptsMid: Array<[number, number]> = [];
        for (const row of data) {
          const ts = parseTs(row.timestamp);
          if (!Number.isFinite(ts)) continue;
          const v = (row as Record<string, unknown>)[label];
          if (!v || typeof v !== 'object') continue;
          const box = v as Boxplot;
          if (typeof box.low === 'number' && typeof box.high === 'number' && ts !== null) {
            const q1 = typeof box.q1 === 'number' ? box.q1 : box.low;
            const q3 = typeof box.q3 === 'number' ? box.q3 : box.high;
            const med = typeof box.median === 'number' ? box.median : (q1 + q3) / 2;
            boxData.push([ts, box.low, q1, med, q3, box.high]);
            if (Number.isFinite(med)) ptsMid.push([ts, med]);
          }
        }
        if (boxData.length) {
          emit({
            type: 'boxplot',
            name: displayName,
            data: boxData.map(d => [d[0], d[1], d[2], d[3], d[4], d[5]]),
            itemStyle: { color: color, borderColor: color },
            yAxisIndex: axisIndexFor.get(label) ?? 0,
            tooltip: { valueFormatter: (val: unknown) => `${val}${unit ? ' ' + unit : ''}` },
          }, isOverlay);

          if (shouldTrend(isOverlay) && ptsMid.length > 1) {
            const lr = linReg(ptsMid);
            if (lr) {
              emit({
                type: 'line',
                name: displayName + ' (trend)',
                data: [[lr.x0, lr.m * lr.x0 + lr.b], [lr.x1, lr.m * lr.x1 + lr.b]],
                lineStyle: { color: color, type: 'dashed', width: 2 },
                symbol: 'none',
                yAxisIndex: axisIndexFor.get(label) ?? 0,
                tooltip: { show: false },
              }, isOverlay);
            }
          }
        }
        return;
      }

      if (shape === 'arearange') {
        const ptsLow: Array<[number, number]> = [];
        const ptsHigh: Array<[number, number]> = [];
        const ptsMid: Array<[number, number]> = [];
        for (const row of data) {
          const ts = parseTs(row.timestamp);
          if (!Number.isFinite(ts)) continue;
          const v = (row as Record<string, unknown>)[label];
          if (!v || typeof v !== 'object') continue;
          const box = v as Boxplot;
          if (typeof box.low === 'number' && typeof box.high === 'number') {
            ptsLow.push([ts as number, box.low]);
            ptsHigh.push([ts as number, box.high]);
            const mid = typeof box.median === 'number' ? box.median : (box.low + box.high) / 2;
            if (Number.isFinite(mid)) ptsMid.push([ts as number, mid]);
          }
        }
        if (ptsLow.length) {
          emit({
            type: 'line',
            name: displayName + ' (low)',
            data: ptsLow,
            lineStyle: { color: color, type: 'dashed', width: 1 },
            symbol: 'none',
            yAxisIndex: axisIndexFor.get(label) ?? 0,
            tooltip: { valueFormatter: (val: unknown) => `${val}${unit ? ' ' + unit : ''}` },
          }, isOverlay);
          emit({
            type: 'line',
            name: displayName + ' (high)',
            data: ptsHigh,
            lineStyle: { color: color, type: 'dashed', width: 1 },
            symbol: 'none',
            yAxisIndex: axisIndexFor.get(label) ?? 0,
            tooltip: { valueFormatter: (val: unknown) => `${val}${unit ? ' ' + unit : ''}` },
          }, isOverlay);
          if (!isOverlay && ptsMid.length) {
            emit({
              type: 'line',
              name: displayName + ' (avg)',
              data: ptsMid,
              lineStyle: { color: color, width: 2 },
              symbol: 'circle',
              symbolSize: 4,
              yAxisIndex: axisIndexFor.get(label) ?? 0,
              tooltip: { valueFormatter: (val: unknown) => `${val}${unit ? ' ' + unit : ''}` },
            }, isOverlay);
          }
          if (shouldTrend(isOverlay) && ptsMid.length > 1) {
            const lr = linReg(ptsMid);
            if (lr) {
              emit({
                type: 'line',
                name: displayName + ' (trend)',
                data: [[lr.x0, lr.m * lr.x0 + lr.b], [lr.x1, lr.m * lr.x1 + lr.b]],
                lineStyle: { color: color, type: 'dashed', width: 2 },
                symbol: 'none',
                yAxisIndex: axisIndexFor.get(label) ?? 0,
                tooltip: { show: false },
              }, isOverlay);
            }
          }
        }
        return;
      }

      const pts: Array<[number, number]> = [];
      for (const row of data) {
        const ts = parseTs(row.timestamp);
        if (!Number.isFinite(ts)) continue;
        const v = (row as Record<string, unknown>)[label];
        if (v && typeof v === 'object') {
          const box = v as Boxplot;
          if (typeof box.median === 'number') pts.push([ts as number, box.median]);
        } else if (typeof v === 'number') {
          pts.push([ts as number, v]);
        }
      }
      if (!pts.length) return;

      if (shape === 'bar') {
        emit({
          type: 'bar',
          name: displayName,
          data: pts,
          itemStyle: { color: color },
          yAxisIndex: axisIndexFor.get(label) ?? 0,
          tooltip: { valueFormatter: (val: unknown) => `${val}${unit ? ' ' + unit : ''}` },
        }, isOverlay);
      } else {
        emit({
          type: 'line',
          name: displayName,
          data: pts,
          lineStyle: { color: color, width: 2 },
          symbol: 'circle',
          symbolSize: 4,
          step: shape === 'step' ? 'end' : undefined,
          yAxisIndex: axisIndexFor.get(label) ?? 0,
          tooltip: { valueFormatter: (val: unknown) => `${val}${unit ? ' ' + unit : ''}` },
        }, isOverlay);
      }

      if (shouldTrend(isOverlay)) {
        const lr = linReg(pts);
        if (lr) {
          emit({
            type: 'line',
            name: displayName + ' (trend)',
            data: [[lr.x0, lr.m * lr.x0 + lr.b], [lr.x1, lr.m * lr.x1 + lr.b]],
            lineStyle: { color: color, type: 'dashed', width: 2 },
            symbol: 'none',
            yAxisIndex: axisIndexFor.get(label) ?? 0,
            tooltip: { show: false },
          }, isOverlay);
        }
      }
    };

    labels.forEach((lbl) => renderLabel(lbl, false));
    if (overlayList.length) {
      overlayList.forEach((lbl) => renderLabel(lbl, true));
    }

    const allSeries = [...baseSeries, ...overlaySeries];

    return allSeries;
  }, [data, labels, overlayList, shapes, colors, units, displayNameMap, axisIndexFor, showTrends, trendTargets, shapeDefault, overlayLines]);

  const option: EChartsOption = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      grid: {
        left: 60,
        right: yAxes.length > 1 ? 60 : 20,
        top: 40,
        bottom: 60,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: isDark ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)',
        textStyle: { color: isDark ? '#f9fafb' : '#000' },
      },
      legend: {
        show: false,
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: dateOnlyTicks ? '{dd}.{MM}.{yyyy}' : '{dd}.{MM}.{yyyy} {HH}:{mm}',
          color: isDark ? '#e5e7eb' : '#000',
        },
        axisLine: { lineStyle: { color: isDark ? '#6b7280' : '#d1d5db' } },
        splitLine: { lineStyle: { color: isDark ? '#374151' : '#e5e7eb' } },
      },
      yAxis: yAxes,
      series,
    };
  }, [yAxes, series, showLegend, dateOnlyTicks, isDark]);

  const legendItems = useMemo(() => {
    const items: Array<{ name: string; color: string }> = [];
    const seen = new Set<string>();
    
    series.forEach((s) => {
      const name = String(s.name || '');
      if (name && !seen.has(name) && !name.includes('(trend)')) {
        seen.add(name);
        const lineStyle = s.lineStyle as { color?: string } | undefined;
        const itemStyle = s.itemStyle as { color?: string } | undefined;
        const color = String(lineStyle?.color || itemStyle?.color || '#888');
        items.push({ name, color });
      }
    });
    
    return items;
  }, [series]);

  return (
    <Box>
      <ReactECharts
        option={option}
        style={{ height: `${height}px`, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
      {showLegend && legendItems.length > 0 && (
        <Group gap="md" mt="sm" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {legendItems.map((item) => (
            <Group key={item.name} gap={6}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
                {item.name}
              </Text>
            </Group>
          ))}
        </Group>
      )}
    </Box>
  );
};

export default UnifiedEChartsV2;
